import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchLeague,
  fetchDivisionsForSeason,
  fetchLeagueSeason,
  fetchSeasons,
} from '../../store/slices/leagueSlice';
import { acceptProposal } from '../../store/slices/gameSlice';
import { fetchMyTeams } from '../../store/slices/teamSlice';
import { UserRole, Category, SeasonStatus, teamSizeForCategory } from '@sulis-liiga/common';
import type { Division, Team, DivisionStandings } from '@sulis-liiga/common';
import leagueService from '../../services/leagueService';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import DivisionGamesList from '../../components/league/DivisionGamesList';
import type { BannerContext } from '../../utils/gameBanner';
import { CATEGORY_SLUGS, CATEGORY_TO_SLUG } from '../../utils/category';

// ─── Category slug ↔ Category enum ───────────────────────────────────────────
// Defined in utils/category; re-exported here for existing importers.
export { CATEGORY_SLUGS, CATEGORY_TO_SLUG };

const ALL_CATEGORIES: Category[] = [
  Category.MENS_SINGLES,
  Category.WOMENS_SINGLES,
  Category.MENS_DOUBLES,
  Category.WOMENS_DOUBLES,
  Category.MIXED_DOUBLES,
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <svg
        className="animate-spin h-8 w-8 text-primary-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-label="Loading"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

function getPromotionZoneRows(division: Division): number {
  return division.teamsToPromote ?? 0;
}

function getRelegationZoneRows(division: Division): number {
  return division.teamsToRelegate ?? 0;
}

/**
 * Returns the Tailwind background class for a standings row.
 *
 * Green (promotion) is suppressed on the top tier of its ladder — there is no
 * tier above to be promoted into.  Red (relegation) is suppressed on the bottom
 * tier — there is no tier below to be relegated into.  Interior tiers keep both.
 */
function getRowBg(
  index: number,
  total: number,
  toPromote: number,
  toRelegate: number,
  isTopTier: boolean,
  isBottomTier: boolean,
): string {
  if (!isTopTier && toPromote > 0 && index < toPromote) return 'bg-success-50';
  if (!isBottomTier && toRelegate > 0 && index >= total - toRelegate) return 'bg-danger-50';
  return '';
}

/** Format a difference with an explicit sign (+3, 0, -2). */
function withSign(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}

/**
 * Group a flat array of divisions into ladders.
 *
 * Divisions sharing a `divisionGroupId` (or, as a fallback, `baseName`) belong
 * to the same ladder.  Divisions with neither are each treated as a single-tier
 * ladder (keyed by their own id).  Each ladder is returned sorted ascending by
 * `level` (1 = top).
 */
function groupIntoLadders(divisions: Division[]): Division[][] {
  const map = new Map<string, Division[]>();
  for (const d of divisions) {
    const key = d.divisionGroupId ?? d.baseName ?? d.id;
    const bucket = map.get(key) ?? [];
    if (!map.has(key)) map.set(key, bucket);
    bucket.push(d);
  }
  // Sort each ladder by level ascending (level 1 = top)
  Array.from(map.values()).forEach(ladder => ladder.sort((a, b) => a.level - b.level));
  // Return ladders ordered by the minimum level of their top tier
  return Array.from(map.values()).sort((a, b) => a[0].level - b[0].level);
}

// ─── DivisionsPage ────────────────────────────────────────────────────────────

const DivisionsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId: paramLeagueId, category: categorySlug } = useParams<{
    leagueId?: string;
    category?: string;
  }>();

  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { selectedLeagueId } = useSelector((state: RootState) => state.ui);
  const { myTeams } = useSelector((state: RootState) => state.team);
  const {
    currentLeague,
    currentSeason,
    divisions,
    seasons,
    loading: leagueLoading,
  } = useSelector((state: RootState) => state.league);

  // Resolve the league from the route param, falling back to the banner-selected
  // league. This lets the standalone "/divisions" sidebar route (used when the URL
  // is not league-scoped) still render the Divisions page instead of bouncing to
  // the Leagues list.
  const leagueId = paramLeagueId ?? selectedLeagueId ?? undefined;

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  // Local state for async data that doesn't fit neatly into slices
  const [standingsMap, setStandingsMap] = useState<Record<string, DivisionStandings>>({});
  const [pendingTeams, setPendingTeams] = useState<Team[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  // Team ids whose tie-break breakdown row is expanded (progressive disclosure —
  // keeps the table compact and mobile-friendly). Multiple may be open at once.
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const toggleExpanded = (teamId: string) =>
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  // Selected season — defaults to the league's current season; can be switched via the selector.
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | undefined>(undefined);

  // Resolve the selected category from the URL param, defaulting to Men's Singles
  const selectedCategory: Category =
    (categorySlug && CATEGORY_SLUGS[categorySlug]) || Category.MENS_SINGLES;

  // Admin check: league/super admin for the SELECTED league
  const isAdmin =
    isAuthenticated &&
    (user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
      user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR));

  // Viewer context for the per-division games list, so a fixture the viewer is in
  // offers "Propose" / "Approve" via the shared banner (single source of truth).
  const myTeamIds = useMemo(() => new Set(myTeams.map(t => t.id)), [myTeams]);
  const bannerCtx: BannerContext = useMemo(
    () => ({ myTeamIds, userId: user?.id }),
    [myTeamIds, user?.id],
  );

  // One-click approval of a single-slot proposal (multi-slot links to the detail).
  const handleApproveProposal = async (proposalId: string) => {
    try {
      const accepted = await dispatch(acceptProposal({ proposalId, slotIndex: 0 })).unwrap();
      navigate(accepted.scheduledGameId ? `/games/${accepted.scheduledGameId}` : '/games');
    } catch {
      // The proposal detail page surfaces errors; nothing inline here.
    }
  };

  // ─── Effects ───────────────────────────────────────────────────────────────

  // 1. Fetch the league entity + all seasons — only after tenant slug is resolved
  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchSeasons(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  // Initialise the selected season for every viewer: prefer the active season, then
  // the upcoming season (so all users can preview what's next), then the most recent
  // completed one. Upcoming seasons are no longer admin-only here.
  useEffect(() => {
    if (selectedSeasonId) return;
    if (seasons.length === 0) return;
    const active = seasons.find(s => s.status === SeasonStatus.ACTIVE);
    const upcoming = seasons.find(s => s.status === SeasonStatus.UPCOMING);
    const latestCompleted = seasons
      .filter(s => s.status === SeasonStatus.COMPLETED)
      .slice()
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];
    const pick = active ?? upcoming ?? latestCompleted;
    if (pick) setSelectedSeasonId(pick.id);
  }, [seasons, selectedSeasonId]);

  // Load the viewer's teams (for the per-division games list's Propose/Approve
  // actions). League-scoped, so gate on the resolved tenant.
  useEffect(() => {
    if (isAuthenticated && tenantResolved) {
      dispatch(fetchMyTeams());
    }
  }, [dispatch, isAuthenticated, tenantResolved]);

  // 2. Whenever the selected season changes, fetch that season's details
  useEffect(() => {
    if (leagueId && tenantResolved && selectedSeasonId) {
      dispatch(fetchLeagueSeason({ leagueId, seasonId: selectedSeasonId }));
    }
  }, [dispatch, leagueId, tenantResolved, selectedSeasonId]);

  // 3. Once we have the season, fetch divisions for the selected category
  useEffect(() => {
    if (leagueId && tenantResolved && currentSeason?.id) {
      dispatch(
        fetchDivisionsForSeason({
          leagueId,
          seasonId: currentSeason.id,
          category: selectedCategory,
        }),
      );
    }
  }, [dispatch, leagueId, tenantResolved, currentSeason?.id, selectedCategory]);

  // 4. Fetch standings for each division + pending teams for the category
  useEffect(() => {
    if (!leagueId || !tenantResolved || !currentSeason?.id || divisions.length === 0) {
      // Clear old standings when switching categories or seasons
      if (divisions.length === 0) {
        setStandingsMap({});
        setPendingTeams([]);
      }
      return;
    }

    // Only fetch standings for divisions in the selected category
    const categoryDivisions = divisions.filter(d => d.category === selectedCategory);
    if (categoryDivisions.length === 0) {
      setStandingsMap({});
      return;
    }

    setDataLoading(true);
    setDataError(null);

    const seasonId = currentSeason.id;
    Promise.all([
      leagueService.getDivisionStandingsBatch(
        leagueId,
        seasonId,
        categoryDivisions.map(d => d.id),
      ),
      leagueService
        .getPendingTeamsForCategory(leagueId, selectedCategory, seasonId)
        .catch(() => [] as Team[]),
    ])
      .then(([standings, teams]) => {
        const map: Record<string, DivisionStandings> = {};
        standings.forEach(s => {
          map[s.divisionId] = s;
        });
        setStandingsMap(map);
        setPendingTeams(teams);
      })
      .catch(err => {
        setDataError(err instanceof Error ? err.message : 'Failed to load standings');
      })
      .finally(() => {
        setDataLoading(false);
      });
  }, [leagueId, tenantResolved, currentSeason?.id, divisions, selectedCategory]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCategoryChange = (cat: Category) => {
    const slug = CATEGORY_TO_SLUG[cat];
    navigate(`/leagues/${leagueId}/divisions/${slug}`);
  };

  // ─── Computed ──────────────────────────────────────────────────────────────

  const categoryDivisions = divisions
    .filter(d => d.category === selectedCategory)
    .sort((a, b) => a.level - b.level);

  // Ladder groups within the selected category — used for the standings section.
  const ladders = useMemo(() => groupIntoLadders(categoryDivisions), [categoryDivisions]);

  // Pending teams whose SIZE matches this category (singles = 1 player, doubles/
  // mixed = 2). A doubles pair can be pending under Men's Doubles or Mixed, so we
  // gate by size — not by an exact team-type match (which would exclude a Doubles
  // team from the Mixed list).
  const categorySize = teamSizeForCategory(selectedCategory);
  const eligiblePendingTeams = pendingTeams.filter(
    team => (team.players?.length ?? 0) === categorySize,
  );

  const totalTeamsInCategory =
    categoryDivisions.reduce((sum, d) => {
      const s = standingsMap[d.id];
      return sum + (s?.standings?.length ?? 0);
    }, 0) + eligiblePendingTeams.length;

  const isLoading = leagueLoading || dataLoading;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page header */}
      <div className="pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Divisions</h1>
            {(currentLeague?.name || !tenantResolved) && (
              <p className="text-sm text-gray-500 mt-1">
                {currentLeague?.name ?? '…'}
                {currentSeason ? ` · ${currentSeason.name}` : ''}
              </p>
            )}
          </div>
          {/* Season selector */}
          {seasons.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="season-select" className="text-sm font-medium text-gray-700 shrink-0">
                Season:
              </label>
              <select
                id="season-select"
                value={selectedSeasonId ?? ''}
                onChange={e => {
                  setSelectedSeasonId(e.target.value || undefined);
                }}
                className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-1.5 bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                aria-label="Select season"
              >
                <option value="">— select season —</option>
                {seasons
                  .filter(
                    s =>
                      s.status === SeasonStatus.ACTIVE ||
                      s.status === SeasonStatus.COMPLETED ||
                      s.status === SeasonStatus.UPCOMING,
                  )
                  .map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.status === SeasonStatus.COMPLETED ? ' (completed)' : ''}
                      {s.status === SeasonStatus.UPCOMING ? ' (upcoming)' : ''}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming-season notice — shown to everyone when previewing a season that
          hasn't started, so provisional divisions/placements are clearly flagged. */}
      {currentSeason?.status === SeasonStatus.UPCOMING && (
        <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4" role="status">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Upcoming season.</span> This season hasn&apos;t started
            yet — divisions and team placements shown here are provisional and may change before it
            begins.
          </p>
        </div>
      )}

      {/* Two-column layout */}
      <div className="mt-6 flex flex-col md:flex-row gap-6">
        {/* ── Category vertical tabs ─────────────────────────────────────── */}
        <nav className="md:w-56 shrink-0" aria-label="Categories">
          {/* Mobile: select dropdown */}
          <select
            className="md:hidden w-full rounded-md border border-gray-300 text-sm shadow-sm px-3 py-2 bg-white mb-2"
            value={selectedCategory}
            onChange={e => handleCategoryChange(e.target.value as Category)}
            aria-label="Select category"
          >
            {ALL_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          {/* Desktop: vertical tab list */}
          <div
            className="hidden md:flex md:flex-col bg-white rounded-lg shadow-sm overflow-hidden"
            role="tablist"
            aria-label="Categories"
          >
            {ALL_CATEGORIES.map(cat => {
              const isActive = cat === selectedCategory;
              const slug = CATEGORY_TO_SLUG[cat];
              return (
                <Link
                  key={cat}
                  to={`/leagues/${leagueId}/divisions/${slug}`}
                  role="tab"
                  aria-current={isActive ? 'true' : undefined}
                  aria-selected={isActive}
                  className={`px-4 py-3 text-sm font-medium border-l-4 transition-colors ${
                    isActive
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-transparent text-gray-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {cat}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* ── Selected category content ────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          {/* Category header */}
          <section className="bg-white rounded-lg shadow-sm p-5 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{selectedCategory}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {categoryDivisions.length} division{categoryDivisions.length !== 1 ? 's' : ''} ·{' '}
                {totalTeamsInCategory} team{totalTeamsInCategory !== 1 ? 's' : ''}
              </p>
            </div>
            {isAdmin && (
              <Link
                to={`/leagues/${leagueId}/divisions/${CATEGORY_TO_SLUG[selectedCategory]}/manage${
                  selectedSeasonId ? `?seasonId=${selectedSeasonId}` : ''
                }`}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Manage
              </Link>
            )}
          </section>

          {/* Loading state */}
          {isLoading && <Spinner />}

          {/* Error state */}
          {!isLoading && dataError && (
            <div className="mt-4 rounded-md bg-danger-50 p-4">
              <p className="text-sm text-danger-600">{dataError}</p>
              <button
                type="button"
                className="mt-2 text-sm text-danger-600 underline"
                onClick={() => {
                  setDataError(null);
                  setDataLoading(true);
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Content when loaded */}
          {!isLoading && !dataError && (
            <>
              {/* No season configured */}
              {!currentLeague?.currentSeasonId && (
                <div className="mt-4 bg-white rounded-lg shadow-sm p-6 text-center text-gray-500">
                  No active season configured for this league.
                </div>
              )}

              {/* Season present but no divisions */}
              {currentLeague?.currentSeasonId &&
                categoryDivisions.length === 0 &&
                eligiblePendingTeams.length === 0 && (
                  <div className="mt-4 bg-white rounded-lg shadow-sm p-6 text-center text-gray-500">
                    No divisions set up for {selectedCategory} yet.
                    {!isAuthenticated && (
                      <p className="mt-1 text-sm">
                        <Link to="/login" className="text-primary-600 hover:underline">
                          Log in
                        </Link>{' '}
                        to register your team.
                      </p>
                    )}
                  </div>
                )}

              {/* Legend — shown when there are divisions */}
              {categoryDivisions.length > 0 && (
                <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <span
                      className="inline-block w-3 h-3 rounded bg-success-100"
                      aria-hidden="true"
                    />
                    Promotion zone
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className="inline-block w-3 h-3 rounded bg-danger-100"
                      aria-hidden="true"
                    />
                    Relegation zone
                  </span>
                </div>
              )}

              {/* Per-ladder standings — tiers within a ladder are grouped together */}
              {ladders.map(ladder => {
                const ladderKey = ladder[0].divisionGroupId ?? ladder[0].baseName ?? ladder[0].id;
                const ladderName = ladder[0].baseName ?? ladder[0].name;

                return (
                  <div key={ladderKey} className="mt-4">
                    {/* Ladder heading when there are multiple tiers or an explicit baseName */}
                    {(ladder.length > 1 || ladder[0].baseName) && (
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        {ladderName}
                      </h3>
                    )}

                    {ladder.map((division, tierIdx) => {
                      const isTopTier = tierIdx === 0;
                      const isBottomTier = tierIdx === ladder.length - 1;
                      const standings = standingsMap[division.id];
                      const rows = standings?.standings ?? [];
                      const toPromote = getPromotionZoneRows(division);
                      const toRelegate = getRelegationZoneRows(division);

                      return (
                        <section key={division.id} className="mt-2">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-700">
                              {division.name}
                              {isTopTier && ladder.length > 1 && (
                                <span className="text-gray-400 font-normal ml-1">· top tier</span>
                              )}
                              {isBottomTier && ladder.length > 1 && (
                                <span className="text-gray-400 font-normal ml-1">
                                  · bottom tier
                                </span>
                              )}
                            </h4>
                            {isAdmin &&
                              currentSeason?.status === 'active' &&
                              leagueId &&
                              currentSeason?.id && (
                                <Link
                                  to={`/leagues/${leagueId}/seasons/${currentSeason.id}/divisions/${division.id}/games/manage`}
                                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                                >
                                  Modify games
                                </Link>
                              )}
                          </div>
                          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
                            {rows.length === 0 ? (
                              <p className="px-4 py-3 text-sm text-gray-500">
                                No teams assigned yet.
                              </p>
                            ) : (
                              <table
                                className="min-w-full text-sm"
                                aria-label={`${division.name} standings`}
                              >
                                <thead className="bg-gray-50 text-gray-500">
                                  <tr>
                                    <th className="text-left px-3 py-2 font-medium w-10">#</th>
                                    <th className="text-left px-3 py-2 font-medium">Team</th>
                                    <th
                                      className="px-2 sm:px-3 py-2 font-medium text-center"
                                      title="Played"
                                    >
                                      P
                                    </th>
                                    <th
                                      className="px-2 sm:px-3 py-2 font-medium text-center"
                                      title="Won"
                                    >
                                      W
                                    </th>
                                    <th
                                      className="px-2 sm:px-3 py-2 font-medium text-center"
                                      title="Lost"
                                    >
                                      L
                                    </th>
                                    <th
                                      className="px-2 sm:px-3 py-2 font-medium text-center font-bold"
                                      title="Points"
                                    >
                                      Pts
                                    </th>
                                    <th className="px-1 py-2 w-8">
                                      <span className="sr-only">Tie-break details</span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {rows.map((standing, idx) => {
                                    const isOpen = expandedTeams.has(standing.teamId);
                                    const gd = standing.pointDifference ?? 0;
                                    const pd =
                                      (standing.pointsWon ?? 0) - (standing.pointsLost ?? 0);
                                    const rowBg = getRowBg(
                                      idx,
                                      rows.length,
                                      toPromote,
                                      toRelegate,
                                      isTopTier,
                                      isBottomTier,
                                    );
                                    return (
                                      <React.Fragment key={standing.teamId}>
                                        <tr className={rowBg}>
                                          <td className="px-3 py-2">{standing.rank}</td>
                                          <td className="px-3 py-2 font-medium">
                                            <Link
                                              to={`/teams/${standing.teamId}`}
                                              className="text-primary-600 hover:underline"
                                            >
                                              {standing.teamName}
                                            </Link>
                                          </td>
                                          <td className="px-2 sm:px-3 py-2 text-center">
                                            {standing.played}
                                          </td>
                                          <td className="px-2 sm:px-3 py-2 text-center">
                                            {standing.won}
                                          </td>
                                          <td className="px-2 sm:px-3 py-2 text-center">
                                            {standing.lost}
                                          </td>
                                          <td className="px-2 sm:px-3 py-2 text-center font-semibold">
                                            {standing.points}
                                          </td>
                                          <td className="px-1 py-2 text-center">
                                            <button
                                              type="button"
                                              onClick={() => toggleExpanded(standing.teamId)}
                                              aria-expanded={isOpen}
                                              aria-label={`Tie-break details for ${standing.teamName}`}
                                              className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            >
                                              <svg
                                                className={`h-4 w-4 transition-transform ${
                                                  isOpen ? 'rotate-180' : ''
                                                }`}
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            </button>
                                          </td>
                                        </tr>
                                        {isOpen && (
                                          <tr className={rowBg}>
                                            <td colSpan={7} className="px-3 pb-3 pt-0">
                                              <div className="rounded-md bg-gray-50 px-3 py-2">
                                                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                                                  <span>
                                                    <span className="text-gray-500">Game diff</span>{' '}
                                                    <span className="font-semibold text-gray-800">
                                                      {withSign(gd)}
                                                    </span>{' '}
                                                    <span className="text-gray-400">
                                                      ({standing.gamesWon ?? 0}–
                                                      {standing.gamesLost ?? 0})
                                                    </span>
                                                  </span>
                                                  <span>
                                                    <span className="text-gray-500">
                                                      Point diff
                                                    </span>{' '}
                                                    <span className="font-semibold text-gray-800">
                                                      {withSign(pd)}
                                                    </span>{' '}
                                                    <span className="text-gray-400">
                                                      ({standing.pointsWon ?? 0}–
                                                      {standing.pointsLost ?? 0})
                                                    </span>
                                                  </span>
                                                </div>
                                                <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
                                                  Teams level on points are ranked by game
                                                  difference, then point difference, then
                                                  head-to-head.
                                                </p>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>

                          {/* All games in this division (fixtures + scheduled + played) */}
                          <DivisionGamesList
                            divisionId={division.id}
                            bannerCtx={bannerCtx}
                            onApproveProposal={handleApproveProposal}
                          />
                        </section>
                      );
                    })}
                  </div>
                );
              })}

              {/* Pending division assignment */}
              {eligiblePendingTeams.length > 0 && (
                <section className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Pending division assignment{' '}
                    <span className="text-gray-400 font-normal">· awaiting admin placement</span>
                  </h3>
                  <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                    {eligiblePendingTeams.map(team => (
                      <div key={team.id} className="px-4 py-3 flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-700">
                          <Link
                            to={`/teams/${team.id}`}
                            className="text-primary-600 hover:underline"
                          >
                            {team.name}
                          </Link>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Footnote */}
              {(categoryDivisions.length > 0 || eligiblePendingTeams.length > 0) && (
                <p className="mt-4 text-xs text-gray-400">
                  Standings are public. Promotion/relegation moves teams between divisions within
                  this category. New teams stay in "Pending division assignment" until an admin
                  places them.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DivisionsPage;
